import { useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '~/hooks/useBreakpoint';

type UseOverflowNavigationParams<T extends string> = {
  items: T[];
  getLabel: (item: T) => string;
  isActive?: (item: T) => boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  measureRef: React.RefObject<HTMLDivElement | null>;
  itemMeasureClassName: string;
  moreButtonMeasureClassName: string;
  moreLabel?: string;
  reservedEndWidthPx?: number;
  /** Enable lightweight console.debug logs for measurement; avoids spam via throttling */
  debug?: boolean;
  /** Short label to identify where logs come from (e.g., "SitesNav") */
  debugLabel?: string;
  /** When true, dumps per-item widths for deeper diagnosis */
  debugDumpItems?: boolean;
};

type UseOverflowNavigationReturn<T extends string> = {
  visibleItems: T[];
  overflowItems: T[];
};

const arraysEqual = <T>(a: readonly T[], b: readonly T[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Computes which navigation items fit within a container and which should go under a “More” menu.
 * - Batches measurement DOM writes/reads to minimize layouts.
 * - Skips measurement on mobile/tablet (under desktopMinWidthPx) to avoid unnecessary work.
 */
export function useOverflowNavigation<T extends string>(
  params: UseOverflowNavigationParams<T>
): UseOverflowNavigationReturn<T> {
  const {
    items,
    getLabel,
    isActive,
    containerRef,
    measureRef,
    itemMeasureClassName,
    reservedEndWidthPx = 0,
    debug = false,
    debugLabel = 'OverflowNav',
    debugDumpItems = false,
  } = params;

  const [visibleItems, setVisibleItems] = useState<T[]>([...items]);
  const [overflowItems, setOverflowItems] = useState<T[]>([]);

  const { isMobile } = useBreakpoint();
  const lastLogTimeRef = useRef<number>(0);
  const canLog = () => {
    if (!debug) return false;
    const now = performance.now();
    if (now - lastLogTimeRef.current > 250) {
      lastLogTimeRef.current = now;
      return true;
    }
    return false;
  };
  const log = (...args: any[]) => {
    if (!debug) return;
    if (!canLog()) return;

    console.debug(`[${debugLabel}]`, ...args);
  };

  // No hysteresis: we compute fit directly from current measurements

  useEffect(() => {
    const containerEl = containerRef.current;
    const measureEl = measureRef.current;
    if (!containerEl || !measureEl) return;

    let scheduled = false;
    let resizeObserver: ResizeObserver | null = null;

    const compute = () => {
      if (!containerRef.current || !measureRef.current) return;

      // Skip measurement on mobile/tablet as indicated by useBreakpoint
      if (isMobile) {
        if (!arraysEqual(visibleItems, items)) setVisibleItems(items);
        if (overflowItems.length !== 0) setOverflowItems([]);
        log('skip on mobile: items=', items.length);
        return;
      }

      const container = containerRef.current;
      const measureContainer = measureRef.current;

      // Viewport + document diagnostics
      log('viewport', {
        innerWidth: window.innerWidth,
        outerWidth: window.outerWidth,
        devicePixelRatio: window.devicePixelRatio,
      });
      log('document', {
        docClientWidth: document.documentElement?.clientWidth,
        docScrollWidth: document.documentElement?.scrollWidth,
        bodyClientWidth: document.body?.clientWidth,
        bodyScrollWidth: document.body?.scrollWidth,
      });
      const availableWidthRaw = Math.max(0, container.clientWidth);
      const containerRectW = container.getBoundingClientRect().width;
      const parent = container.parentElement;
      const parentClientWidth = parent?.clientWidth;
      const parentOffsetWidth = parent?.offsetWidth;
      const parentRectWidth = parent?.getBoundingClientRect().width;
      // Subtract the container's right margin from the available width because
      // clientWidth does not include margins, but layout space is reduced by it.
      const computedStyle = window.getComputedStyle(container);
      const marginRightPx = Number.parseFloat(computedStyle.marginRight) || 0;
      const availableWidth = Math.max(
        0,
        availableWidthRaw - (reservedEndWidthPx || 0) - marginRightPx
      );
      if (availableWidth <= 0) return;
      // Read horizontal gap applied to the container (flex/grid column-gap)
      const columnGapRaw = window.getComputedStyle(container).columnGap;
      const gapPx = Number.parseFloat(columnGapRaw) || 0;

      log('measure', {
        containerW: container.clientWidth,
        containerRectW,
        parentClientWidth,
        parentOffsetWidth,
        parentRectWidth,
        marginRightPx,
        reservedEndWidthPx,
        availableWidth,
        gapPx,
        items: items.length,
      });

      // Build all measurement nodes off-DOM first
      measureContainer.innerHTML = '';
      const frag = document.createDocumentFragment();

      const createdMeasureNodes: HTMLElement[] = [];
      for (const item of items) {
        const el = document.createElement('div');
        el.className =
          itemMeasureClassName + (isActive?.(item) ? ' font-semibold' : '');
        el.textContent = getLabel(item);
        frag.appendChild(el);
        createdMeasureNodes.push(el);
      }

      measureContainer.appendChild(frag);

      // Now measure widths in one read phase
      const widths = createdMeasureNodes.map(
        (el) => el.getBoundingClientRect().width
      );
      const moreWidth = MORE_BUTTON_WIDTH_PX;

      // No buffer or hysteresis: compute exact fit
      const widthsTotal = widths.reduce((sum, w) => sum + w, 0);
      const gapsTotal = Math.max(items.length - 1, 0) * gapPx;
      const totalItemsWidth = widthsTotal + gapsTotal;
      if (debugDumpItems) {
        log(
          'widths',
          widths.map((w) => Number(w.toFixed(1))),
          'sum',
          widthsTotal.toFixed(1),
          'gaps',
          gapsTotal.toFixed(1),
          'more',
          moreWidth
        );
      } else {
        log('widthsSummary', {
          sum: widthsTotal.toFixed(1),
          gaps: gapsTotal.toFixed(1),
          count: widths.length,
          more: moreWidth,
        });
      }
      // Always reserve space for the More button to keep right-side spacing consistent

      if (totalItemsWidth <= availableWidth - moreWidth) {
        const allVisible: T[] = [...items];
        if (!arraysEqual(visibleItems, allVisible)) setVisibleItems(allVisible);
        if (overflowItems.length !== 0) setOverflowItems([]);
        if (debug) {
          const now = performance.now();
          if (now - lastLogTimeRef.current > 400) {
            lastLogTimeRef.current = now;

            console.debug(
              `[${debugLabel}] fit-all: avail=${availableWidth.toFixed(
                1
              )} gap=${gapPx} total=${totalItemsWidth.toFixed(1)} items=${
                items.length
              }`
            );
          }
        }
        return;
      }

      let used = 0; // accumulated width of visible items plus inter-item gaps
      let fitCount = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const nextWidth = widths[i];
        const withNext = used + (fitCount > 0 ? gapPx : 0) + nextWidth;
        // If more items remain after including this one, we also need space for the gap + More button
        const requiresMore = i + 1 < widths.length;
        // Always reserve the More button width; include gap only if another item would follow
        const requiredWidth = withNext + moreWidth + (requiresMore ? gapPx : 0);
        if (requiredWidth <= availableWidth) {
          used = withNext;
          fitCount += 1;
        } else {
          break;
        }
      }

      if (fitCount >= widths.length) {
        const allVisible: T[] = [...items];
        if (!arraysEqual(visibleItems, allVisible)) setVisibleItems(allVisible);
        if (overflowItems.length !== 0) setOverflowItems([]);
        if (debug) {
          const now = performance.now();
          if (now - lastLogTimeRef.current > 400) {
            lastLogTimeRef.current = now;

            console.debug(
              `[${debugLabel}] fit-all (loop): avail=${availableWidth.toFixed(
                1
              )} fitCount=${fitCount}`
            );
          }
        }
        return;
      }

      const baseVisible: T[] = items.slice(0, fitCount);
      // Ensure the active item remains visible where possible
      if (fitCount > 0 && isActive) {
        const activeIndex = items.findIndex((it) => isActive(it));
        if (activeIndex >= fitCount && activeIndex !== -1) {
          log('activeSwap', { fitCount, activeIndex });
          baseVisible[fitCount - 1] = items[activeIndex];
        }
      }

      const baseOverflow: T[] = items.filter((t) => !baseVisible.includes(t));

      if (!arraysEqual(visibleItems, baseVisible)) setVisibleItems(baseVisible);
      if (!arraysEqual(overflowItems, baseOverflow))
        setOverflowItems(baseOverflow);

      log('fit-partial', {
        availableWidth: Number(availableWidth.toFixed(1)),
        gapPx,
        moreWidth,
        fitCount,
        items: items.length,
        overflow: baseOverflow.length,
        reservedEndWidthPx,
      });
    };

    const scheduleCompute = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        compute();
      });
    };

    scheduleCompute();

    resizeObserver = new ResizeObserver(() => scheduleCompute());
    resizeObserver.observe(containerEl);

    const onResize = () => scheduleCompute();
    window.addEventListener('resize', onResize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Effect intentionally excludes setState setters and derived state to avoid infinite loops; latest overflow state is tracked via hasOverflowRef.
  }, [
    items,
    getLabel,
    isActive,
    containerRef,
    measureRef,
    itemMeasureClassName,
    isMobile,
    reservedEndWidthPx,
  ]);

  return { visibleItems, overflowItems };
}

// Shared constant width for the "More" button used across navs
export const MORE_BUTTON_WIDTH_PX = 93;
